import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import site from "../data/site";
import logo from "../assets/logo.webp";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 bg-[#E293AB] ${
        scrolled
          ? "shadow-[0_2px_12px_0_rgba(0,0,0,0.12)]"
          : ""
      }`}
    >
      <div className="container-luxe flex items-center justify-between py-2 md:py-2.5">
        <Link
          to="/"
          data-testid="brand-link"
          className="flex items-center group"
          aria-label="Eternal Care, Home"
        >
          <img
            src={logo}
            alt="Eternal Care — Greater Houston, TX"
            className="h-16 md:h-20 w-auto object-contain"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[0.86rem] tracking-[0.14em] uppercase font-medium link-soft ${
                  isActive ? "text-charcoal font-semibold" : "text-charcoal/75 hover:text-charcoal"
                }`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={site.phoneHref}
            data-testid="header-call-btn"
            className="btn-pill btn-ghost"
          >
            <Phone className="w-4 h-4" strokeWidth={1.5} />
            {site.phone}
          </a>
        </div>

        <button
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          className="lg:hidden grid place-items-center w-10 h-10 rounded-full bg-white/80 text-rose-ink hover:bg-white transition-colors"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 ease-out ${
          open ? "max-h-[520px] opacity-100" : "max-h-0 opacity-0"
        }`}
        aria-hidden={!open}
      >
        <div className="container-luxe pb-6 pt-2">
          <ul className="flex flex-col gap-1 border-t border-line pt-4">
            {navItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `block px-3 py-3 rounded-md font-display text-2xl ${
                      isActive ? "text-charcoal font-semibold" : "text-charcoal"
                    } hover:bg-white/40 transition-colors`
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
            <li className="pt-2">
              <a
                href={site.phoneHref}
                data-testid="mobile-call-btn"
                className="btn-pill btn-primary w-full justify-center"
              >
                <Phone className="w-4 h-4" /> Call {site.phone}
              </a>
            </li>
          </ul>
        </div>
      </div>
    </header>
  );
}
